import { DirectUpload } from "@rails/activestorage"
import { CloudUpload, FileVideo, ImageIcon, X } from "lucide-react"
import { useCallback, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"]
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"]
const IMAGE_MAX_SIZE = 50 * 1024 * 1024 // 50MB
const VIDEO_MAX_SIZE = 500 * 1024 * 1024 // 500MB

interface FileUploadProps {
  accept: "image" | "video"
  onUploadComplete: (signedId: string, file: File) => void
  onUploadError?: (error: string) => void
  directUploadUrl?: string
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(bytes / 1024).toFixed(0)} KB`
}

export default function FileUpload({
  accept,
  onUploadComplete,
  onUploadError,
  directUploadUrl = "/rails/active_storage/direct_uploads",
}: FileUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [progress, setProgress] = useState<number>(0)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const allowedTypes = accept === "image" ? IMAGE_TYPES : VIDEO_TYPES
  const maxSize = accept === "image" ? IMAGE_MAX_SIZE : VIDEO_MAX_SIZE
  const acceptAttr =
    accept === "image" ? ".jpg,.jpeg,.png,.webp" : ".mp4,.mov,.webm"

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!allowedTypes.includes(file.type)) {
        return "Tipo de archivo no permitido"
      }
      if (file.size > maxSize) {
        return `Archivo demasiado grande (máx. ${formatFileSize(maxSize)})`
      }
      return null
    },
    [allowedTypes, maxSize],
  )

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        setSelectedFile(null)
        setPreview(null)
        return
      }

      setError(null)
      setSelectedFile(file)

      if (accept === "image") {
        const reader = new FileReader()
        reader.onload = (e) => setPreview(e.target?.result as string)
        reader.readAsDataURL(file)
      }

      // Start direct upload
      setUploading(true)
      setProgress(0)

      const upload = new DirectUpload(file, directUploadUrl, {
        directUploadWillStoreFileWithXHR(xhr: XMLHttpRequest) {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              setProgress(Math.round((event.loaded / event.total) * 100))
            }
          })
        },
      })

      upload.create((uploadError, blob) => {
        setUploading(false)
        if (uploadError) {
          setError("Error al subir el archivo")
          onUploadError?.("Error al subir el archivo")
          return
        }
        if (blob) {
          setProgress(100)
          onUploadComplete(blob.signed_id, file)
        }
      })
    },
    [accept, directUploadUrl, onUploadComplete, onUploadError, validateFile],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleClear = useCallback(() => {
    setSelectedFile(null)
    setPreview(null)
    setProgress(0)
    setError(null)
    setUploading(false)
    if (inputRef.current) inputRef.current.value = ""
  }, [])

  return (
    <div className="w-full">
      <input
        ref={inputRef}
        data-testid="file-input"
        type="file"
        accept={acceptAttr}
        onChange={handleInputChange}
        className="hidden"
      />

      {!selectedFile && !error && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => inputRef.current?.click()}
          className="border-border bg-secondary/50 hover:border-krown-300 hover:bg-secondary flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors"
        >
          {accept === "image" ? (
            <ImageIcon className="text-muted-foreground h-10 w-10" />
          ) : (
            <FileVideo className="text-muted-foreground h-10 w-10" />
          )}
          <div>
            <p className="text-foreground text-sm">
              Arrastrá tu archivo acá o{" "}
              <span className="text-krown-300 font-medium">
                elegí un archivo
              </span>
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              {accept === "image"
                ? `JPG, PNG, WEBP — máx. ${formatFileSize(IMAGE_MAX_SIZE)}`
                : `MP4, MOV, WEBM — máx. ${formatFileSize(VIDEO_MAX_SIZE)}`}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-red-400">{error}</p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleClear}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <button
            type="button"
            onClick={() => {
              handleClear()
              inputRef.current?.click()
            }}
            className="text-krown-300 mt-1 text-xs underline"
          >
            Intentar de nuevo
          </button>
        </div>
      )}

      {selectedFile && !error && (
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-start gap-3">
            {accept === "image" && preview ? (
              <img
                src={preview}
                alt="Vista previa"
                className="h-16 w-16 rounded object-cover"
              />
            ) : (
              <div className="bg-secondary flex h-16 w-16 items-center justify-center rounded">
                {accept === "image" ? (
                  <ImageIcon className="text-muted-foreground h-8 w-8" />
                ) : (
                  <FileVideo className="text-muted-foreground h-8 w-8" />
                )}
              </div>
            )}

            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{selectedFile.name}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleClear}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">
                {formatFileSize(selectedFile.size)}
              </p>

              {uploading && (
                <div className="mt-2">
                  <Progress value={progress} className="h-1.5" />
                  <p className="text-muted-foreground mt-1 text-xs">
                    <CloudUpload className="mr-1 inline h-3 w-3" />
                    Subiendo... {progress}%
                  </p>
                </div>
              )}

              {!uploading && progress === 100 && (
                <p className="mt-1 text-xs text-green-400">Subido</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
